import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import DatePicker from 'react-datepicker';
import { ToastContainer } from 'react-toastify';
import ToasterHelper from '../../shared/helpers/toaster.helper';
import { ISignUp } from '../../models/interfaces/signup.interface';
import Request from '../../shared/request/Request';
import { API } from '../../shared/constants/API';

import 'react-datepicker/dist/react-datepicker.css';
import { HttpStatusCode } from '../../shared/request/HttpStatusCode';
import Header from '../main-page/header';
import { FiInfo } from 'react-icons/fi';
import { isUserOlderThan15, minPasswordLength } from '../../shared/helpers/utils.helper';

type FormValues = {
  name: string;
  email: string;
  password: string;
  dateOfBirth: string;
  countryId: number;
};

function CompleteProcess() {
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormValues>();
  const queryParams = new URLSearchParams(window.location.search);
  const email: string | null = queryParams.get('email');

  const { t } = useTranslation();
  const [isSuccess, setIsSuccess] = useState(false);
  const [validated, setValidated] = useState(false);
  const [emailInvalid] = useState(false);
  const [passwordInvalid, setPasswordInvalid] = useState(false);
  const [country, setCountry] = useState(0);
  const [countries, setCountries] = useState([]);

  const [dateOfBirth, setDateOfBirth] = useState('');
  const [userDateOfBirth, setUserDateOfBirth] = useState(null);
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);

  var years = [];
  const startingYear = 1930;
  for (var i = startingYear; i <= 2100; i++) {
    years.push(i);
  }
  // TODO - Add translations
  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  useEffect(() => {
    if (countries.length === 0) {
      Request.get(`${API.USERS_SERVICE}/getCountries`, {})
        .then((response: any) => {
          if (response.body?.result) {
            setCountries(response.body.result);
          } else {
            if (response.status.toString() === HttpStatusCode.TOO_MANY_REQUESTS) {
              ToasterHelper.notify(t('ERRORS.TRY_AGAIN_LATER'), 'info');
            }
            return [];
          }
        })
        .catch(error => {
          ToasterHelper.notify(t('ERRORS.AN_ERROR_OCCURRED'), 'error');
          console.log(error);
          return [];
        });
    }
  }, [countries, t]);

  const handleUserDateOfBirthSelect = (date: any) => {
    let dt: any = '';

    if (date != null) {
      dt = `${date.getFullYear()}-${date.getMonth() + 1 < 10 ? '0' : ''}${date.getMonth() + 1}-${
        date.getDate() + 1 < 10 ? '0' : ''
      }${date.getDate()}`;
    }
    if (isUserOlderThan15(date)) {
      setDateOfBirth(dt);
      setSubmitButtonDisabled(false);
    } else {
      setSubmitButtonDisabled(true);
      ToasterHelper.notify(t('COMMON.USER_OLDER_THAN_15'), 'error');
    }
  };

  const onSignUp = (data: any) => {
    setSubmitButtonDisabled(true);
    setIsSuccess(false);

    if (data.name && data.email && data.password && country > 0 && dateOfBirth.length === 10 && isUserOlderThan15(dateOfBirth)) {
      const input: ISignUp = {
        name: data.name,
        email: data.email,
        password: data.password,
        dateOfBirth: dateOfBirth,
        countryId: country
      };

      Request.post(`${API.USERS_SERVICE}/completeProcess`, { body: input })
        .then((result: any) => {
          if (result.body.message === 'USER_ALREADY_REGISTERED') {
            ToasterHelper.notify(t('COMPLETE_PROCESS.USER_ALREADY_REGISTERED_MESSAGE'), 'warn');
          } else if (result.body.message === 'OK') {
            setIsSuccess(true);
          } else {
            ToasterHelper.notify(t('COMPLETE_PROCESS.FAILED_MESSAGE'), 'error');
          }
        })
        .catch((error: any) => {
          ToasterHelper.notify(t('COMPLETE_PROCESS.FAILED_MESSAGE'), 'error');
          console.log(error);
        })
        .finally(() => {
          setSubmitButtonDisabled(false);
        });
    } else {
      setSubmitButtonDisabled(false);
    }
    setValidated(true);
  };

  const validatePassword = (event: any) => {
    setPasswordInvalid(false);
    if (event.target.value.length < minPasswordLength) {
      setPasswordInvalid(true);
    }
    if (!/[A-Z]/.test(event.target.value)) {
      setPasswordInvalid(true);
    }
    if (!/[£$%&#?!,.]/.test(event.target.value)) {
      setPasswordInvalid(true);
    }
    event.preventDefault();
  };

  return (
    <>
      <div>
        <section className='background'>
          <Header showLinks={true} />
          {!isSuccess && (
            <>
              <div className='pt-20'>
                <p className='text-center text-white'>
                  <b>{t('COMPLETE_PROCESS.TITLE')}</b>
                </p>
              </div>
              <ToastContainer />{' '}
              <Form noValidate validated={validated} onSubmit={handleSubmit(onSignUp)}>
                <div className='flex flex-col items-center'>
                  <div className='col-md-2 text-sm'>
                    <div className='col-md-3 text-white'>{t('COMPLETE_PROCESS.USER_NAME') + ' *'}</div>
                    <div className='col-md-9'>
                      <Form.Group controlId='name' className='min-w-80 form-column'>
                        <Form.Control
                          type='text'
                          required
                          placeholder={t('COMPLETE_PROCESS.USER_NAME') + ' *'}
                          maxLength={50}
                          {...register('name', {
                            required: t('COMPLETE_PROCESS.VALIDATIONS.USER_NAME_REQUIRED'),
                            minLength: {
                              value: 4,
                              message: t('COMPLETE_PROCESS.VALIDATIONS.USER_NAME_MIN_LENGTH')
                            }
                          })}
                        />
                        <div>{errors.name && <span className='text-white'>{errors.name.message}</span>}</div>
                      </Form.Group>
                    </div>
                  </div>
                  <div className='col-md-2 text-sm mt-3'>
                    <div className='col-md-3 text-white'>{t('COMMON.COUNTRY') + ' *'}</div>
                    <div className='col-md-9'>
                      <select
                        onChange={e => setCountry(parseInt(e.target.value, 10))}
                        className='border-1 rounded-1 text-sm min-h-8 min-w-80 pl-2 bg-white'
                        required>
                        <option key='country.id_country.name' value=''></option>
                        {countries.map(country => (
                          <option key={country.id + country.name} value={country.id}>
                            {country.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className='col-md-2 text-sm mt-3'>
                    <div className='col-md-4 text-white'>{t('COMPLETE_PROCESS.USER_DATE_OF_BIRTH') + ' *'}</div>
                    <div className='flex col-md-4'>
                      <div
                        className='col-md-4 border-1 rounded-1 min-w-52 bg-white'
                        style={{
                          zIndex: 9999
                        }}>
                        <DatePicker
                          calendarClassName='calendar'
                          placeholderText={`${t('COMPLETE_PROCESS.USER_DATE_OF_BIRTH') + ' *'}`}
                          required
                          showIcon
                          className='calendar'
                          disabledKeyboardNavigation
                          selected={userDateOfBirth}
                          onInputClick={() => {
                            setSubmitButtonDisabled(true);
                          }}
                          onChange={setUserDateOfBirth}
                          onSelect={handleUserDateOfBirthSelect}
                          dateFormat={'yyyy-MM-dd'}
                          renderCustomHeader={({ date, changeYear, changeMonth }) => (
                            <div
                              style={{
                                margin: 10,
                                display: 'flex',
                                justifyContent: 'center',
                                zIndex: 9999
                              }}>
                              <select
                                value={date.getFullYear()}
                                onChange={({ target: { value } }) => changeYear(years[parseInt(value, 10) - startingYear])}
                                style={{
                                  borderRadius: 5,
                                  width: 60,
                                  height: 30,
                                  zIndex: 9999
                                }}>
                                {years.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={months[date.getMonth()]}
                                onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
                                style={{
                                  marginLeft: 30,
                                  borderRadius: 5,
                                  width: 100,
                                  height: 30,
                                  zIndex: 9999
                                }}>
                                {months.map(option => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}
                        />
                      </div>
                      <div className='col-md-4 text-white text-base mt-2 pl-2'>
                        <span title='We will use your date of birth to calculate your age group.'>
                          <FiInfo />
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className='col-md-2 text-sm mt-3'>
                    <div className='col-md-3 text-white'>{t('COMPLETE_PROCESS.USER_EMAIL') + ' *'}</div>
                    <div className='col-md-9'>
                      <Form.Group controlId='email' className='min-w-80 form-column'>
                        <Form.Control
                          type='email'
                          required
                          value={email}
                          placeholder={t('COMPLETE_PROCESS.USER_EMAIL') + ' *'}
                          disabled={true}
                          maxLength={100}
                          {...register('email', {
                            required: t('COMPLETE_PROCESS.VALIDATIONS.USER_EMAIL_REQUIRED')
                          })}
                        />
                        <div>{errors.email && <span className='text-white'>{errors.email.message}</span>}</div>
                        <div>
                          {emailInvalid && <small className='text-danger'>{t('COMPLETE_PROCESS.VALIDATIONS.USER_EMAIL_VALID')}</small>}
                        </div>
                      </Form.Group>
                    </div>
                  </div>
                  <div className='col-md-2 text-sm mt-3'>
                    <div className='col-md-3 text-white'>{t('COMPLETE_PROCESS.PASSWORD') + ' *'}</div>
                    <div className='col-md-9'>
                      <Form.Group controlId='password' className='min-w-80 form-column'>
                        <Form.Control
                          type='password'
                          required
                          onChange={validatePassword}
                          placeholder={t('COMPLETE_PROCESS.PASSWORD') + ' *'}
                          minLength={minPasswordLength}
                          maxLength={50}
                          {...register('password', {
                            required: t('COMPLETE_PROCESS.VALIDATIONS.PASSWORD_REQUIRED')
                          })}
                        />
                        <div>
                          <small className='text-sm text-white' dangerouslySetInnerHTML={{ __html: t('COMMON.PASSWORD_RULES') }} />
                        </div>
                        <div>
                          {passwordInvalid && <small className='text-danger'>{t('COMPLETE_PROCESS.VALIDATIONS.PASSWORD_REQUIRED')}</small>}
                        </div>
                      </Form.Group>
                    </div>
                  </div>
                </div>
                <div className='text-center mt-4 pb-4'>
                  <Button
                    role='submit-button'
                    className='button-font-size background-complete-process button-border-radius cursor-pointer'
                    variant='primary'
                    type='submit'
                    disabled={submitButtonDisabled}>
                    <b>{t('COMPLETE_PROCESS.SUBMIT')}</b>
                  </Button>
                </div>
              </Form>
            </>
          )}
          {isSuccess && (
            <div className='items-center text-white text-center pt-30 pb-20'>
              <b>{t('COMPLETE_PROCESS.SUCCESS_MESSAGE')}</b>
            </div>
          )}
        </section>
      </div>
    </>
  );
}

export default CompleteProcess;
